import { useState, useEffect } from 'react';
import { sortByName } from '../lib/sortByName';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import {
    Hotel, Star, Wifi, Gift, ExternalLink, Search, Loader2, BarChart3,
    AlertTriangle, TrendingDown, MapPin, MessageSquare, Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import HotelAnalytics from '../components/HotelAnalytics';
import { RoomFeedbackResults } from '../components/hotel/RoomFeedbackResults';

const TOUCHPOINT_LABELS = {
    welcome: 'Wi-Fi / Room',
    lobby_wifi: 'Wi-Fi / Lobby',
    checkout: 'Checkout',
    in_stay: 'Bathroom / In-Stay',
    room_feedback: 'Welcome / Room',
};

const HotelResults = () => {
    const { t } = useTranslation();
    const { isSuperAdmin } = useAuth();
    const [sessions, setSessions] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRating, setFilterRating] = useState('all');
    const [filterOutcome, setFilterOutcome] = useState('all');
    const [filterLocation, setFilterLocation] = useState('all');
    const [filterTouchpoint, setFilterTouchpoint] = useState('all');
    const [organizations, setOrganizations] = useState([]);
    const [filterOrganization, setFilterOrganization] = useState('all');

    useEffect(() => { loadData(); }, [filterOrganization, filterTouchpoint]);

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            // Same safe single-record endpoint the Review Shield trash uses
            await api.delete(`/review-shield/feedback/${deleteTarget.id}`);
            setSessions(prev => prev.filter(s => s.id !== deleteTarget.id));
            toast.success(t('hotel.recordDeleted', 'Guest record deleted'));
        } catch (err) {
            toast.error(err.response?.data?.detail || t('hotel.recordDeleteFailed', 'Failed to delete record'));
        } finally {
            setDeleting(false);
            setDeleteTarget(null);
        }
    };

    const loadData = async () => {
        try {
            setLoading(true);
            const params = {};
            if (filterOrganization !== 'all') params.organization_id = filterOrganization;
            if (filterTouchpoint !== 'all') params.touchpoint_type = filterTouchpoint;

            const [sessionsRes, summaryRes] = await Promise.all([
                api.get('/hotel/admin/sessions', { params }),
                api.get('/hotel/admin/summary', { params }),
            ]);
            setSessions(sessionsRes.data);
            setSummary(summaryRes.data);

            if (isSuperAdmin()) {
                try {
                    const orgsRes = await api.get('/organizations');
                    setOrganizations(sortByName(orgsRes.data));
                } catch (e) { /* ignored */ }
            }
        } catch (err) {
            toast.error('Failed to load hotel data');
        } finally {
            setLoading(false);
        }
    };

    const locations = [...new Set(sessions.map(s => s.location_name).filter(Boolean))];
    const touchpoints = [...new Set(sessions.map(s => s.touchpoint_type).filter(Boolean))];

    const filtered = sessions.filter(s => {
        if (filterRating !== 'all') {
            if (filterRating === 'low' && (s.rating || 5) > 3) return false;
            if (filterRating === 'high' && (s.rating || 0) < 4) return false;
        }
        if (filterOutcome !== 'all' && s.outcome !== filterOutcome) return false;
        if (filterLocation !== 'all' && s.location_name !== filterLocation) return false;
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            return (
                (s.guest_email || '').toLowerCase().includes(term) ||
                (s.location_name || '').toLowerCase().includes(term) ||
                (s.rating_category || '').toLowerCase().includes(term) ||
                (s.touchpoint_name || '').toLowerCase().includes(term)
            );
        }
        return true;
    });

    const formatDate = (ts) => {
        if (!ts) return '-';
        const d = new Date(ts);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    const isRealEmail = (email) => email && !email.endsWith('@guest.local') && !email.endsWith('@anonymous') && email.length > 0;

    const renderStars = (count) => {
        if (!count) return <span className="text-slate-500 text-xs">-</span>;
        return (
            <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map(s => (
                    <Star key={s} className="w-3.5 h-3.5"
                        fill={s <= count ? '#FBBF24' : 'transparent'}
                        stroke={s <= count ? '#FBBF24' : '#94a3b8'}
                        strokeWidth={1.5} />
                ))}
            </div>
        );
    };

    const touchpointBadge = (tp) => {
        const styles = {
            welcome: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
            lobby_wifi: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
            checkout: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
            in_stay: 'bg-orange-500/10 text-orange-400 border-orange-500/30',
            room_feedback: 'bg-teal-500/10 text-teal-400 border-teal-500/30',
        };
        const label = TOUCHPOINT_LABELS[tp] || tp || '-';
        return (
            <Badge variant="outline" className={`text-xs ${styles[tp] || 'bg-slate-500/10 text-slate-400'}`}>
                {label}
            </Badge>
        );
    };

    const outcomeBadge = (outcome) => {
        const styles = {
            both: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
            gift_only: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
            review_only: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
            private_feedback: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
            neither: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
        };
        const labels = {
            both: t('hotel.giftAndPublicRating', 'Gift + Public Rating Clicked'),
            gift_only: t('hotel.giftOnly', 'Gift Only'),
            review_only: t('hotel.publicRatingClicked', 'Public Rating Clicked'),
            private_feedback: t('hotel.privateFeedback', 'Private Feedback'),
            neither: t('hotel.neither', 'Neither'),
        };
        return (
            <Badge variant="outline" className={`text-xs ${styles[outcome] || styles.neither}`}>
                {labels[outcome] || outcome || '-'}
            </Badge>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="hotel-results">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-foreground flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <Hotel className="w-6 h-6 text-amber-500" />
                        {t('hotel.resultsTitle', 'Hotel Guest Results')}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {t('hotel.resultsSubtitle', 'Guest interactions collected through Hotel Review Shield')}
                    </p>
                </div>
            </div>

            {/* Summary Cards */}
            {summary && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card><CardContent className="p-4">
                        <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                            <BarChart3 className="w-3.5 h-3.5" />{t('hotel.totalSessions', 'Total Sessions')}
                        </div>
                        <p className="text-2xl font-bold text-foreground">{summary.total}</p>
                    </CardContent></Card>
                    <Card><CardContent className="p-4">
                        <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                            <Star className="w-3.5 h-3.5" />{t('hotel.avgScore', 'Avg. Score')}
                        </div>
                        <p className="text-2xl font-bold text-foreground">{summary.avg_score}<span className="text-sm text-muted-foreground"> /5</span></p>
                    </CardContent></Card>
                    <Card><CardContent className="p-4">
                        <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                            <AlertTriangle className="w-3.5 h-3.5" />{t('hotel.lowRatings', 'Low Ratings')}
                        </div>
                        <p className="text-2xl font-bold text-red-500">{summary.low_ratings}</p>
                    </CardContent></Card>
                    <Card><CardContent className="p-4">
                        <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                            <TrendingDown className="w-3.5 h-3.5" />{t('hotel.topCategory', 'Top Category')}
                        </div>
                        <p className="text-2xl font-bold text-foreground capitalize">{summary.top_category}</p>
                    </CardContent></Card>
                </div>
            )}

            {/* Analytics */}
            <HotelAnalytics organizationId={filterOrganization} touchpointType={filterTouchpoint} />

            {/* Room Feedback — independently filterable experience results */}
            {(filterTouchpoint === 'all' || filterTouchpoint === 'room_feedback') && (
                <RoomFeedbackResults organizationId={filterOrganization} />
            )}

            {/* Filters */}
            <div className="flex flex-wrap gap-3 items-center">
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        placeholder={t('hotel.searchPlaceholder', 'Search email, location...')}
                        className="pl-9 h-9"
                        data-testid="hotel-search-input"
                    />
                </div>
                {isSuperAdmin() && organizations.length > 0 && (
                    <Select value={filterOrganization} onValueChange={setFilterOrganization}>
                        <SelectTrigger className="w-[180px] h-9" data-testid="hotel-filter-org">
                            <SelectValue placeholder={t('common.organization')} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('common.all')} {t('common.organization', 'Organizations')}</SelectItem>
                            {organizations.map(o => (
                                <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
                <Select value={filterTouchpoint} onValueChange={setFilterTouchpoint}>
                    <SelectTrigger className="w-[170px] h-9" data-testid="hotel-filter-touchpoint">
                        <SelectValue placeholder={t('hotel.touchpoint', 'Touchpoint')} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('hotel.allTouchpoints', 'All Touchpoints')}</SelectItem>
                        <SelectItem value="room_feedback">{t('hotel.touchpointRoomFeedback', 'Welcome / Room')}</SelectItem>
                        <SelectItem value="welcome">{t('hotel.touchpointWelcome', 'Wi-Fi / Room')}</SelectItem>
                        <SelectItem value="lobby_wifi">{t('hotel.touchpointLobbyWifi', 'Wi-Fi / Lobby')}</SelectItem>
                        <SelectItem value="checkout">{t('hotel.touchpointCheckout', 'Checkout')}</SelectItem>
                        <SelectItem value="in_stay">{t('hotel.touchpointInStay', 'Bathroom / In-Stay')}</SelectItem>
                    </SelectContent>
                </Select>
                {locations.length > 0 && (
                    <Select value={filterLocation} onValueChange={setFilterLocation}>
                        <SelectTrigger className="w-[160px] h-9" data-testid="hotel-filter-location">
                            <SelectValue placeholder={t('common.location')} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('common.all')} Locations</SelectItem>
                            {locations.map(l => (
                                <SelectItem key={l} value={l}>{l}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
                <Select value={filterRating} onValueChange={setFilterRating}>
                    <SelectTrigger className="w-[140px] h-9" data-testid="hotel-filter-rating">
                        <SelectValue placeholder="Rating" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('common.all')} Ratings</SelectItem>
                        <SelectItem value="high">4-5 Stars</SelectItem>
                        <SelectItem value="low">1-3 Stars</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={filterOutcome} onValueChange={setFilterOutcome}>
                    <SelectTrigger className="w-[180px] h-9" data-testid="hotel-filter-outcome">
                        <SelectValue placeholder="Outcome" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('common.all')} Outcomes</SelectItem>
                        <SelectItem value="both">{t('hotel.giftAndPublicRating', 'Gift + Public Rating Clicked')}</SelectItem>
                        <SelectItem value="gift_only">{t('hotel.giftOnly', 'Gift Only')}</SelectItem>
                        <SelectItem value="review_only">{t('hotel.publicRatingClicked', 'Public Rating Clicked')}</SelectItem>
                        <SelectItem value="private_feedback">{t('hotel.privateFeedback', 'Private Feedback')}</SelectItem>
                        <SelectItem value="neither">{t('hotel.neither', 'Neither')}</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Results Table */}
            <Card>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('common.date', 'Date')}</TableHead>
                                    <TableHead>{t('hotel.touchpoint', 'Touchpoint')}</TableHead>
                                    <TableHead>{t('common.location', 'Location')}</TableHead>
                                    <TableHead>{t('hotel.ratingCol', 'Rating')}</TableHead>
                                    <TableHead>{t('hotel.categoryCol', 'Category')}</TableHead>
                                    <TableHead>{t('common.email', 'Email')}</TableHead>
                                    <TableHead><Wifi className="w-3.5 h-3.5 inline mr-1" />Wi-Fi</TableHead>
                                    <TableHead><Gift className="w-3.5 h-3.5 inline mr-1" />{t('hotel.giftCap', 'Gift')}</TableHead>
                                    <TableHead><ExternalLink className="w-3.5 h-3.5 inline mr-1" />{t('hotel.publicRatingCap', 'Public Rating')}</TableHead>
                                    <TableHead>{t('hotel.outcomeCol', 'Outcome')}</TableHead>
                                    <TableHead className="w-10"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filtered.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                                            {t('hotel.noSessions', 'No hotel guest sessions yet')}
                                        </TableCell>
                                    </TableRow>
                                ) : filtered.map(s => (
                                    <TableRow key={s.id} data-testid={`hotel-session-row-${s.id}`}>
                                        <TableCell className="text-xs whitespace-nowrap">{formatDate(s.created_at)}</TableCell>
                                        <TableCell>{touchpointBadge(s.touchpoint_type)}</TableCell>
                                        <TableCell className="text-sm">{s.location_name || '-'}</TableCell>
                                        <TableCell>{renderStars(s.rating)}</TableCell>
                                        <TableCell className="text-sm capitalize">{s.rating_category || '-'}</TableCell>
                                        <TableCell>
                                            {isRealEmail(s.guest_email) ? (
                                                <span className="text-xs text-muted-foreground">{s.guest_email}</span>
                                            ) : (
                                                <Badge variant="outline" className="text-xs bg-slate-500/10 text-slate-400">N/A</Badge>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className={`text-xs ${s.wifi_sent_at ? 'bg-blue-500/10 text-blue-400 border-blue-500/30' : 'bg-slate-500/10 text-slate-400'}`}>
                                                {s.wifi_sent_at ? t('hotel.sent', 'Sent') : t('common.no', 'No')}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className={`text-xs ${s.gift_claimed ? 'bg-amber-500/10 text-amber-400 border-amber-500/30' : 'bg-slate-500/10 text-slate-400'}`}>
                                                {s.gift_claimed ? t('hotel.claimed', 'Claimed') : t('common.no', 'No')}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className={`text-xs ${s.review_click ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' : 'bg-slate-500/10 text-slate-400'}`}>
                                                {s.review_click ? (s.review_destination || t('common.yes', 'Yes')) : t('common.no', 'No')}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>{outcomeBadge(s.outcome)}</TableCell>
                                        <TableCell>
                                            {s.rating != null && (
                                                <Button variant="ghost" size="icon"
                                                    className="h-7 w-7 text-muted-foreground hover:text-red-500"
                                                    onClick={() => setDeleteTarget(s)}
                                                    data-testid={`hotel-delete-record-${s.id}`}>
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <AlertDialogContent data-testid="hotel-delete-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('hotel.deleteRecordTitle', 'Delete this guest record?')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {deleteTarget && (
                                <>
                                    {t('hotel.deleteRecordDesc', 'You are deleting ONE individual guest journey record:')}
                                    <span className="block mt-2 text-foreground text-sm">
                                        {formatDate(deleteTarget.created_at)} · {deleteTarget.location_name || '-'} · {deleteTarget.rating}★
                                        {isRealEmail(deleteTarget.guest_email) ? ` · ${deleteTarget.guest_email}` : ''}
                                    </span>
                                    <span className="block mt-2">
                                        {t('hotel.deleteRecordNote', 'Only this record is affected. Other journeys, sessions, vouchers and analytics data remain untouched.')}
                                    </span>
                                </>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="hotel-delete-cancel">{t('common.cancel', 'Cancel')}</AlertDialogCancel>
                        <AlertDialogAction onClick={confirmDelete} disabled={deleting}
                            className="bg-red-600 hover:bg-red-700 text-white"
                            data-testid="hotel-delete-confirm">
                            {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
                            {t('common.delete', 'Delete')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default HotelResults;
